/**
 * Dr. Herança Tenant - API Endpoints
 * 
 * This file contains all API endpoints specific to the Dr. Herança tenant.
 */

import { emptySplitApi as api } from './baseApi';

const injectedRtkApi = api
  .enhanceEndpoints({ addTagTypes: ['Inventory', 'Emolumento'] })
  .injectEndpoints({
  endpoints: (build) => ({
    createInventory: build.mutation<CreateInventoryApiResponse, CreateInventoryApiArg>({
      query: (queryArg) => ({
        url: `CInventario`,
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: queryArg
      }),
      invalidatesTags: [{ type: 'Inventory', id: 'LIST' }],
    }),
    getInventories: build.query<GetInventoryApiResponse, GetInventoryApiArg>({
      query: (queryArg) => ({
        url: `CInventario`,
        method: 'GET',
        headers: {
          'content-type': 'application/json'
        },
        params: {
          // maxSize: queryArg.maxSize,
          offset: queryArg.offset,
          orderBy: queryArg.orderBy,
          order: queryArg.order
        }
      }),
      providesTags: (result) => 
        result ? 
          [
            ...result.list.map(({ id }: { id: string }) => ({ type: 'Inventory' as const, id })),
            { type: 'Inventory', id: 'LIST' },
          ]
        : 
          [{ type: 'Inventory', id: 'LIST' }],
    }),
    updateInventory: build.mutation<UpdateInventoryApiResponse, UpdateInventoryApiArg>({
      query: (queryArg) => ({
        url: `CInventario/${queryArg.id}`,
        method: 'PUT',
        headers: {
          'content-type': 'application/json'
        },
        body: {
          cliente: queryArg.cliente,
          name: queryArg.name,
          json: queryArg.json
        }
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Inventory', id }],
    }),
    deleteInventory: build.mutation<DeleteInventoryApiResponse, string>({
      query: (id) => ({
        url: `CInventario/${id}`,
        method: 'DELETE',
        headers: {
          'content-type': 'application/json'
        }
      }),
      invalidatesTags: [{ type: 'Inventory', id: 'LIST' }],
    }),
    getInventoryById: build.query<GetInventoryApiResponse, string>({
      query: (id) => ({
        url: `CInventario/${id}`,
        method: 'GET',
        headers: {
          'content-type': 'application/json'
        }
      }),
      providesTags: (_result, _error, id) => [{ type: 'Inventory', id }],
    }),
    updateUser: build.mutation<UpdateUserApiResponse, UpdateUserApiArg>({
      query: (queryArg) => ({
        url: `User/${queryArg.id}`,
        method: 'PUT',
        headers: {
          'content-type': 'application/json'
        },
        body: queryArg.body
      })
    }),
    updatePassword: build.mutation<UpdatePasswordApiResponse, UpdatePasswordApiArg>({
      query: (queryArg) => ({
        url: `UserSecurity/password`,
        method: 'PUT',
        headers: {
          'content-type': 'application/json'
        },
        body: queryArg.body
      })
    }),
    getCharges: build.query<GetChargesApiResponse, GetChargesApiArg>({
      query: (queryArg) => ({
        url: `CCobrancas`,
        method: 'GET',
        headers: {
          'content-type': 'application/json'
        },
        params: {
          // maxSize: queryArg.maxSize,
          offset: queryArg.offset,
          orderBy: queryArg.orderBy,
          order: queryArg.order,
        }
      })
    }),
    getBlogArticles: build.query<GetBlogArticlesApiResponse, GetBlogArticlesApiArg>({
      query: (queryArg) => ({
        url: `CArtigoBlog`,
        method: 'GET',
        headers: {
          'accept': '*/*',
          'content-type': 'application/json',
          'authorization': 'Basic dXNlcnNpdGU6SWRfcVc1bTgzSEw='
        },
        params: {
          // maxSize: queryArg.maxSize,
          offset: queryArg.offset,
          orderBy: queryArg.orderBy,
          order: queryArg.order,
          attributeSelect: queryArg.attributeSelect,
          'whereGroup[0][type]': 'in',
          'whereGroup[0][attribute]': 'status',
          'whereGroup[0][value][]': 'Published'
        }
      })
    }),
    getBlogArticleById: build.query<GetBlogArticleByIdApiResponse, string>({
      query: (id) => ({
        url: `CArtigoBlog/${id}`,
        method: 'GET',
        headers: {
          'accept': '*/*',
          'content-type': 'application/json',
          'authorization': 'Basic dXNlcnNpdGU6SWRfcVc1bTgzSEw='
        },
      })
    }),
    getPlans: build.query<GetPlansApiResponse, GetPlansApiArg>({
      query: (queryArg) => ({
        url: `CPlano`,
        method: 'GET',
        headers: {
          'accept': '*/*',
          'content-type': 'application/json',
          'authorization': 'Basic dXNlcnNpdGU6SWRfcVc1bTgzSEw='
        },
        params: {
          // maxSize: queryArg.maxSize,
          offset: queryArg.offset,
          orderBy: queryArg.orderBy,
          order: queryArg.order,
        }
      })
    }),
    getEmolumentos: build.query<GetEmolumentosApiResponse, GetEmolumentosApiArg>({
      query: (queryArg) => ({
        url: `CEmolumento`,
        method: 'GET',
        headers: {
          'content-type': 'application/json'
        },
        params: {
          maxSize: queryArg.maxSize,
          offset: queryArg.offset,
          orderBy: queryArg.orderBy,
          order: queryArg.order,
          attributeSelect: queryArg.attributeSelect,
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.list.map(({ id }: { id: string }) => ({ type: 'Emolumento' as const, id })),
              { type: 'Emolumento', id: 'LIST' },
            ]
          : [{ type: 'Emolumento', id: 'LIST' }],
    })
  }),
  overrideExisting: false
});

export { injectedRtkApi as drherancaApi };

// API Types
export type CreateInventoryApiArg = {
  name: string;
  cliente: string;
  json: string;
  assignedUserName?: string | null;
  assignedUserId?: string | null;
  teamsIds?: string[];
  teamsNames?: Record<string, string>;
};

export type CreateInventoryApiResponse = {
  status?: number;
  message?: string;
  id?: string;
};

export type GetInventoryApiArg = {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
};

export type GetInventoryApiResponse = any;

export type UpdateInventoryApiArg = {
  id: string;
  cliente: string;
  name: string;
  json: string;
};

export type UpdateInventoryApiResponse = {
  status?: number;
  message?: string;
};

export type DeleteInventoryApiResponse = {
  status?: number;
  message?: string;
};

export type UpdateUserApiArg = {
  id: string;
  body: {
    name: string;
    firstName: string;
    lastName: string;
    cAddressStreet: string;
    cAddressCity: string;
    cAddressState: string;
    cAddressCountry: string;
    cAddressPostalCode: string;
  };
};

export type UpdateUserApiResponse = {
  id?: string;
  phoneNumber?: string;
  phoneNumberData?: Array<{ phoneNumber: string; primary: boolean; type: string; optOut: boolean; invalid: boolean }>;
  status?: number;
  message?: string;
};

export type UpdatePasswordApiArg = {
  body: {
    currentPassword?: string;
    password?: string;
  };
};

export type UpdatePasswordApiResponse = any;

export type GetChargesApiArg = {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
};

export type GetChargesApiResponse = {
  list: Array<{
    accessionDate: string;
    assignedUserId: string | null;
    assignedUserName: string | null;
    createdAt: string;
    createdById: string;
    createdByName: string;
    dateNextCharge: string;
    deleted: boolean;
    description: string | null;
    endAccessionDate: string;
    id: string;
    json: string | null;
    modifiedAt: string;
    modifiedById: string;
    modifiedByName: string;
    name: string;
    planName: string;
    planoId: string;
    planoName: string;
    priceValue: number;
    priceValueConverted: number;
    priceValueCurrency: string;
    requestDate: string;
    status: string;
    subscriberEmail: string;
    subscriberName: string;
    transaction: string | null;
    trial: boolean;
  }>;
};

export type GetBlogArticlesApiArg = {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  attributeSelect?: string;
};

export type GetBlogArticlesApiResponse = any;

export type GetBlogArticleByIdApiArg = {
  id: string;
};

export type GetBlogArticleByIdApiResponse = any;

export type GetPlansApiArg = {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
};

export type GetPlansApiResponse = {
  list: Array<{
    id: string;
    name: string;
    description: string;
    createdAt: string;
    priceValue: number;
    linkPayment: string;
    priceValueCurrency: string;
    createdById: string;
    assignedUserId: string | null;
    maxInventories: number;
  }>;
};

export type GetEmolumentosApiArg = {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  /**
   * EspoCRM: lista de atributos separados por vírgula.
   * Ex.: "valorEmolumentosCurrency,valorEmolumentos,valorFinalFaixaCurrency,valorFinalFaixa,valorInicialFaixaCurrency,valorInicialFaixa,name"
   */
  attributeSelect?: string;
};

export type CEmolumentoListItem = {
  id: string;
  deleted?: boolean;
  name?: string;
  /** Valor inicial da faixa (Espo: valorInicialFaixa) */
  valorInicialFaixa?: number | string | null;
  /** Valor final da faixa (Espo: valorFinalFaixa) */
  valorFinalFaixa?: number | string | null;
  /** Valor dos emolumentos (Espo: valorEmolumentos) */
  valorEmolumentos?: number | string | null;
};

export type GetEmolumentosApiResponse = {
  list: CEmolumentoListItem[];
};

export const {
  useCreateUserMutation,
  useLoginEspoCrmMutation,
  useGetInventoriesQuery,
  useGetInventoryByIdQuery,
  useUpdateInventoryMutation,
  useDeleteInventoryMutation,
  useCreateInventoryMutation,
  useGetUserInfoQuery,
  useUpdateUserMutation,
  useUpdatePasswordMutation,
  useGetChargesQuery,
  useGetBlogArticlesQuery,
  useGetBlogArticleByIdQuery,
  useGetPlansQuery,
  useGetEmolumentosQuery
} = injectedRtkApi;
